import { Box, Flex, Grid } from '@/components/atoms/Grid'
import InformationContent from '@/components/atoms/informationContent'
import { Capitalized2, H1, Small2Base } from '@/components/atoms/typography'
import BannersLayout, {
  BannersType
} from '@/components/organisms/bannersLayout'
import PageContentWrapper from '@/components/organisms/pageContentWrapper'
import { HorizontalTitledProductsSlider } from '@/components/organisms/productsSlider'
import {
  CustomType,
  getMhubListPrefix,
  HorizontalTitledProductsSliderFetcher,
  SliderPosition,
  TitleType
} from '@/components/organisms/productsSlider/horizontalTitledProductsSlider'
import TopManufacturerSlider from '@/components/organisms/topManufacturerSlider'
import {
  ShowProductModal,
  withProductDetailPopup
} from '@/HOC/withProductDetailPopup'
import {
  AttributeId,
  Campaign,
  Category,
  FilterType,
  HomeBanner,
  HomeBannerPosition,
  HomeBannerSize,
  Manufacturer,
  Sdk,
  SpecialPageBanner,
  SpecialPageBannerPositionType,
  SpecialPageType
} from '@/network/graphql.g'
import useTranslation from 'next-translate/useTranslation'
import {
  BigAccentButton,
  BigGrayButton,
  BlackButton
} from '@/components/atoms/button'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import { SectionSeparator } from '@/components/atoms/separator'
import { stringify } from 'querystring'
import CategorySlider from '@/components/organisms/categorySlider'
import LandingPageProductSliderHeader from '@/components/molecules/landingPageProductSlideHeader'
import {
  convertApiAttributeIdToAttributeId,
  convertAttributeIdToApiAttributeId
} from '@/types/attributeId'
import useGraphQl from 'hooks/useGraphQl'
import { FilterQueryItem } from '@/types/filterQueryItem'
import { requestThrow401 } from '@/utils/graphQl'
import { withBaseServerSideProps } from '@/HOC/withBaseServerSideProps'
import { useMhub } from '@/providers/mhubProvider'
import { FC, useEffect } from 'react'
import { convertAPIProductToProduct } from '@/types/bridgeProduct'
import { TshirtIconDefault } from '@/components/atoms/icons/TshirtIconDefaut'
import { SweatshirtIconDefault } from '@/components/atoms/icons/SweatshirtIconDefault'
import { TracksuitIconDefault } from '@/components/atoms/icons/TracksuitIconDefault'
import { JeansIconDefault } from '@/components/atoms/icons/JeansIconDefault'
import { ShortsIconDefault } from '@/components/atoms/icons/ShortsIconDefault'
import { JacketIconDefault } from '@/components/atoms/icons/JacketIconDefault'
import { ShirtIconDefault } from '@/components/atoms/icons/ShirtIconDefault'
import { getMainData } from '@/utils/mainDataHelper'
import SweatersWomen from '@/components/atoms/svg/categoryIcons/womensClothing/SweatersWomen'
import SweatersMen from '@/components/atoms/svg/categoryIcons/mensClothing/SweatersMen'
import { SocksIconDefault } from '@/components/atoms/icons/SocksIconDefault'
import { MultiPacksIconDefault } from '@/components/atoms/icons/MultiPackIconDefault'
import { UnderwearWomenIconDefault } from '@/components/atoms/icons/UnderwearWomenIconDefault'
import { UnderwearMenIconDefault } from '@/components/atoms/icons/UnderwearMenIconDefault'
import { SlippersIconDefault } from '@/components/atoms/icons/SlippersIconDefault'

type LandingPageProps = {
  manufacturer: Manufacturer
  categories: Category[]
  specialPageBanners: Record<string, SpecialPageBanner>
  topManufacturers: Manufacturer[]
  isWoman: boolean
  genderIds: [string]
  showProductModal?: ShowProductModal
  isFrogiesLandingPage: boolean
  campaignsByIds: Campaign[]
}

const toHomeBanner = (
  specialPageBanner: SpecialPageBanner,
  size: HomeBannerSize,
  routerPath: string
): HomeBanner & { routerPath?: string } =>
  specialPageBanner
    ? {
        id: specialPageBanner.id,
        imageBig: specialPageBanner.description?.desktopImage,
        imageSmall: specialPageBanner.description?.mobileImage,
        url1: specialPageBanner.bannerUrl,
        size,
        title: specialPageBanner.description?.description,
        position: specialPageBanner.position as unknown as HomeBannerPosition,
        routerPath
      }
    : null

const icons = {
  '250': <TshirtIconDefault />,
  '241': <TshirtIconDefault />,
  '246': <SweatshirtIconDefault />,
  '235': <SweatshirtIconDefault />,
  '44': <TracksuitIconDefault />,
  '64': <TracksuitIconDefault />,
  '1205': <JeansIconDefault />,
  '1225': <JeansIconDefault />,
  '41': <ShortsIconDefault />,
  '70': <ShortsIconDefault />,
  '243': <JacketIconDefault />,
  '234': <JacketIconDefault />,
  '1197': <ShirtIconDefault />,
  '48': <ShirtIconDefault />
}

const frogiesIcons = {
  '875': <UnderwearWomenIconDefault width={32} height={32} />,
  '877': <UnderwearMenIconDefault width={32} height={32} />,
  '880': <SocksIconDefault width={32} height={32} />,
  '881': <SocksIconDefault width={32} height={32} />,
  '883': <MultiPacksIconDefault width={32} height={32} />,
  '884': <MultiPacksIconDefault width={32} height={32} />,
  '885': <SlippersIconDefault width={32} height={32} />,
  '886': <SlippersIconDefault width={32} height={32} />,
  '887': <SweatersWomen width={32} height={32} />,
  '888': <SweatersMen width={32} height={32} />,
  //Deti
  '876': <UnderwearWomenIconDefault width={32} height={32} />,
  '878': <UnderwearMenIconDefault width={32} height={32} />,
  '882': <SocksIconDefault width={32} height={32} />
}

const LandingPage: FC<LandingPageProps> = ({
  manufacturer,
  categories,
  specialPageBanners,
  topManufacturers,
  isWoman,
  genderIds,
  showProductModal,
  isFrogiesLandingPage,
  campaignsByIds
}) => {
  const { t } = useTranslation('common')
  const router = useRouter()
  const { request } = useGraphQl()
  const pageCode = 'landing-page'

  const bestsellersFetcher = async (
    key: string,
    genderIds: [string]
  ): Promise<HorizontalTitledProductsSliderFetcher> => {
    const result = await request(
      (sdk: Sdk) => ({
        method: sdk.getLandingPageBestsellers,
        variables: {
          manufacturerId: manufacturer.id,
          genderIds
        }
      }),
      true
    )
    const algorithm = result.response?.recommended?.[0]?.algorithm

    return {
      list: getMhubListPrefix(pageCode, CustomType.bestSeller, algorithm),
      products:
        result.response?.recommended?.[0]?.products?.map((item) =>
          convertAPIProductToProduct(item)
        ) ?? [],
      algorithm: algorithm
    }
  }

  const inSaleFetcher = async (
    key: string,
    genderIds: [string]
  ): Promise<HorizontalTitledProductsSliderFetcher> => {
    const filter = [
      {
        filterType: FilterType.Gender,
        filterIDs: genderIds
      },
      {
        filterType: FilterType.InSale,
        filterIDs: ['1']
      }
    ]

    const result = await request(
      (sdk: Sdk) => ({
        method: sdk.getLandingPageSaleProducts,
        variables: {
          id: manufacturer.id,
          limit: 24,
          page: 1,
          filter: filter
        }
      }),
      true
    )
    const algorithm = 'recommended_InSale'

    return {
      list: getMhubListPrefix(pageCode, CustomType.insale, algorithm),
      products:
        result.response?.manufacturerProducts.products?.map((item) =>
          convertAPIProductToProduct(item)
        ) ?? [],
      algorithm: algorithm
    }
  }

  const routerPath = router.asPath.split('/').pop()
  const mainBanners: HomeBanner[] = [
    toHomeBanner(
      specialPageBanners[SpecialPageBannerPositionType.MainBanner],
      HomeBannerSize.Big,
      routerPath
    )
  ].filter((item) => item)

  const upBanners: HomeBanner[] = [
    toHomeBanner(
      specialPageBanners[SpecialPageBannerPositionType.UpBannerLeft],
      HomeBannerSize.Small,
      routerPath
    ),
    toHomeBanner(
      specialPageBanners[SpecialPageBannerPositionType.UpBannerRight],
      HomeBannerSize.Small,
      routerPath
    )
  ].filter((item) => item)

  const manufacturerBanners: HomeBanner[] = [
    toHomeBanner(
      specialPageBanners[SpecialPageBannerPositionType.ManufacturerBanner],
      HomeBannerSize.Big,
      routerPath
    )
  ].filter((item) => item)

  const downBanners: HomeBanner[] = [
    toHomeBanner(
      specialPageBanners[SpecialPageBannerPositionType.DownBannerLeft],
      HomeBannerSize.Small,
      routerPath
    ),
    toHomeBanner(
      specialPageBanners[SpecialPageBannerPositionType.DownBannerRight],
      HomeBannerSize.Small,
      routerPath
    )
  ].filter((item) => item)

  const onWomenClick = (e: Event) => {
    e.preventDefault()
    router.push(getUrl(convertApiAttributeIdToAttributeId(AttributeId.Woman)))
  }

  const getUrl = (gender: AttributeId): string => {
    let path = router.asPath
    const index = path.indexOf('?')
    if (index !== -1) {
      path = path.substring(0, index)
    }
    return `${path}?${stringify({ gender: gender.toString() })}`
  }

  const onMenClick = (e: Event) => {
    e.preventDefault()
    router.push(getUrl(convertApiAttributeIdToAttributeId(AttributeId.Man)))
  }

  const { pushLandingPagePage } = useMhub()
  useEffect(() => {
    pushLandingPagePage()
  }, [])

  return (
    <StyledPageContentWrapper isFrogiesLandingPage={isFrogiesLandingPage}>
      {mainBanners.length > 0 && (
        <BannersLayout
          banners={mainBanners}
          bannerDst={BannersType.LandingPage}
        />
      )}
      <Flex
        my={30}
        justifyContent="center"
        flexDirection={{
          desktop: 'row',
          tablet: 'row',
          mobile: 'column'
        }}
      >
        <StyledCapitalized2
          textAlign="center"
          mr={{
            desktop: 40,
            tablet: 40,
            mobile: 0
          }}
        >
          {t('LandingPage.adaptFor')}
        </StyledCapitalized2>
        <Flex>
          <Grid
            width="100%"
            gridTemplateColumns="1fr 1fr"
            gridColumnGap={{
              desktop: 16,
              tablet: 16,
              mobile: 0
            }}
          >
            {isWoman ? (
              <>
                <BigAccentButton
                  minWidth={140}
                  title={t('LandingPage.women')}
                  onClick={onWomenClick}
                />
                <BigGrayButton
                  minWidth={140}
                  title={t('LandingPage.men')}
                  onClick={onMenClick}
                />
              </>
            ) : (
              <>
                <BigGrayButton
                  minWidth={140}
                  title={t('LandingPage.women')}
                  onClick={onWomenClick}
                />
                <BigAccentButton
                  minWidth={140}
                  title={t('LandingPage.men')}
                  onClick={onMenClick}
                />
              </>
            )}
          </Grid>
        </Flex>
      </Flex>
      <SectionSeparator />
      <LandingPageProductSliderHeader
        title={t('LandingPage.topCategories')}
        getUrl={getUrl}
        isWoman={isWoman}
      />
      <CategorySlider
        categories={
          isFrogiesLandingPage
            ? campaignsByIds?.map((item) => ({
                id: item.id,
                title: item.title,
                icon: frogiesIcons[item.id],
                url: item.url
              })) ?? []
            : categories?.map((item) => ({
                id: item.id,
                title: item.title,
                icon: icons[item.id],
                url: `${item.url}?${FilterQueryItem.MANUFACTURER}=${manufacturer.id}`
              })) ?? []
        }
      />
      {/* Bestsellers carousel  */}
      <HorizontalTitledProductsSlider
        action={showProductModal}
        titleType={TitleType.custom}
        customTitle={
          <LandingPageProductSliderHeader
            title={t('LandingPage.bestsellers')}
            getUrl={getUrl}
            isWoman={isWoman}
          />
        }
        slideItemsCount={{ desktop: 5, tablet: 3, mobile: 2 }}
        fetcher={bestsellersFetcher}
        swrKey={['recommended/bestsellers', genderIds]}
        position={SliderPosition.second}
      />

      {/* InSale carousel*/}
      <StyledHorizontalTitledProductsSlider
        action={showProductModal}
        titleType={TitleType.custom}
        customTitle={
          <LandingPageProductSliderHeader
            title={t('LandingPage.InSale')}
            getUrl={getUrl}
            isWoman={isWoman}
          />
        }
        slideItemsCount={{ desktop: 5, tablet: 3, mobile: 2 }}
        fetcher={inSaleFetcher}
        swrKey={['recommended/inSale', genderIds]}
        position={SliderPosition.third}
      />

      {!isFrogiesLandingPage && upBanners.length > 0 && (
        <BannersLayout
          mt={isFrogiesLandingPage ? 16 : 70}
          banners={upBanners}
          bannerDst={BannersType.LandingPage}
          gridColumnGap={16}
          gridRowGap={isFrogiesLandingPage ? 16 : 70}
          isFrogiesLandingPage={isFrogiesLandingPage}
        />
      )}

      {!isFrogiesLandingPage && downBanners.length > 0 && (
        <BannersLayout
          mt={isFrogiesLandingPage ? 16 : 70}
          banners={downBanners}
          bannerDst={BannersType.LandingPage}
          gridColumnGap={16}
          gridRowGap={isFrogiesLandingPage ? 16 : 70}
          isFrogiesLandingPage={isFrogiesLandingPage}
        />
      )}
      {/* About manufacturer */}
      {!isFrogiesLandingPage && (
        <Grid
          my={70}
          gridTemplateColumns={{
            desktop: '1fr 1fr',
            tablet: '1fr',
            mobile: '1fr'
          }}
        >
          {manufacturerBanners.length > 0 && (
            <BannersLayout
              banners={manufacturerBanners}
              bannerDst={BannersType.LandingPage}
            />
          )}
          <Box textAlign="center" px={{ desktop: 40, tablet: 0, mobile: 0 }}>
            <Box textAlign="left">
              <StyledH1>{manufacturer.name}</StyledH1>
              <StyledSmall2>
                <InformationContent html={manufacturer.description} />
              </StyledSmall2>
            </Box>
            <BlackButton
              mt={30}
              title={t('LandingPage.manufacturerLink')}
              onClick={(e: Event) => {
                e.preventDefault()
                router.push(manufacturer.link)
              }}
            />
          </Box>
        </Grid>
      )}
      {!isFrogiesLandingPage && (
        <StyledCapitalized2 textAlign="center" mt={70}>
          {t('LandingPage.topManufacturers')}
        </StyledCapitalized2>
      )}
      {!isFrogiesLandingPage && (
        <TopManufacturerSlider
          hideTitle={true}
          mb={50}
          manufacturers={topManufacturers.filter(
            (manufacturer) => manufacturer.image != null
          )}
        />
      )}
    </StyledPageContentWrapper>
  )
}

export const getServerSideProps = withBaseServerSideProps(
  async ({
    context,
    sdk,
    withMainNavigation,
    withStore
  }): Promise<GetServerSideProps<LandingPageProps>> => {
    const manufacturerId = context.params.slug as string
    const gender =
      convertAttributeIdToApiAttributeId(Number(context.query.gender)) ??
      AttributeId.Woman
    const isWoman = gender === AttributeId.Woman
    const categoryIds = isWoman
      ? ['250', '246', '44', '1205', '41', '243', '1197']
      : ['241', '235', '64', '1225', '70', '234', '48']

    const campaignsIds = isWoman
      ? ['875', '880', '883', '885', '887', '876', '882']
      : ['877', '881', '884', '886', '888', '878', '882']

    const manufacturerIds = [
      '8',
      '117',
      '353',
      '439',
      '477',
      '591',
      '139093',
      '655',
      '681',
      '139593',
      '906'
    ]

    const frogiesLandingPageId = ['142775']

    const isFrogiesLandingPage = frogiesLandingPageId.includes(manufacturerId)

    const { success, response } = await requestThrow401({
      method: sdk.getLandingPage,
      variables: {
        manufacturerId,
        manufacturerIds,
        categoryIds,
        page: SpecialPageType.LandingPage,
        gender,
        withMainNavigation,
        withStore,
        campaignsIds
      },
      successPredicate: (response) =>
        !!(
          response?.manufacturer &&
          response?.categories &&
          response?.manufacturers &&
          response?.specialPage &&
          response?.campaignsByIds
        )
    })
    if (!success) {
      return {
        notFound: true
      }
    }

    return {
      props: {
        mainData: getMainData(response),
        seoTags: response?.seoTags,
        manufacturer: response?.manufacturer,
        categories: response?.categories,
        campaignsByIds: response?.campaignsByIds,
        specialPageBanners: (
          response?.specialPage as SpecialPageBanner[]
        ).reduce(
          (acc, item) => ({ ...acc, [item.position]: item }),
          {} as Record<string, SpecialPageBanner>
        ),
        topManufacturers: response?.manufacturers,
        genderIds: [convertApiAttributeIdToAttributeId(gender)?.toString()],
        isWoman,
        isFrogiesLandingPage,
        disableDaktela: true
      }
    }
  }
)

export default withProductDetailPopup(LandingPage)

const StyledPageContentWrapper = styled(PageContentWrapper)`
  margin-bottom: ${(props) => props.isFrogiesLandingPage && '40px'};
`

const StyledCapitalized2 = styled(Capitalized2)`
  text-transform: uppercase;
`

const StyledH1 = styled(H1)`
  text-transform: lowercase;
  font-size: 47px;
`

const StyledSmall2 = styled.div`
  ${Small2Base}
  line-height: 1.5;
  opacity: 0.6;
  a {
    text-decoration: none;
    color: ${({ theme }) => theme.colors.onBackground};
  }
  a:hover {
    text-decoration: underline;
  }
`

const StyledHorizontalTitledProductsSlider = styled(
  HorizontalTitledProductsSlider
)`
  margin-bottom: 54px;
`
